import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchEquipmentTaskList } from "../../redux/actions/action";
import TruckIcon from "../../assets/images/truck.svg";
import proEquipmentsIcon from "../../assets/icons/proEquipmentsIcon.svg";
import Package from "../../assets/icons/package.svg";
import xIcon from "../../assets/icons/x-circle.svg";
import AppDatePicker from "../Common/AppDatePicker";
import CalenderIcon from "../../assets/icons/Calendar with interaction.svg";
import CheckboxIcon from "../../assets/images/CheckboxWhite.svg";
import CheckboxBlueIcon from "../../assets/images/CheckboxBlue.svg";
import { fetchScreenTranslations } from "../../utils/helpers/translations";

const getCurrentWeek = (date) => {
  if (!date) return null;
  const startOfYear = new Date(date.getFullYear(), 0, 0);
  const diff = date - startOfYear;
  const oneDay = 1000 * 60 * 60 * 24;
  const currentWeek = Math.floor(diff / oneDay / 7) + 1;
  return currentWeek;
};

const getNextWeek = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  // If start and end dates are the same, return the week number of the start date
  if (startDate.getTime() === endDate.getTime()) {
    return getCurrentWeek(startDate);
  }

  const nextWeek = new Date(endDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  const startOfYear = new Date(nextWeek.getFullYear(), 0, 0);
  const diff = nextWeek - startOfYear;
  const oneDay = 1000 * 60 * 60 * 24;
  const nextWeekNumber = Math.floor(diff / oneDay / 7) + 1;
  return nextWeekNumber;
};

const TaskEquipmentList = ({
  setToggleEquipment,
  selectedEquipments,
  setSelectedEquipments,
  onApply,
  singleSelect = false,
  style,
}) => {
  const [fetchTransUpdateTaskList, setFetchTransUpdateTaskList] = useState({});

  useEffect(() => {
    fetchScreenTranslations("Updated-Task").then((res) => {
      setFetchTransUpdateTaskList(res);
    });
  }, []);

  const t = (key) => fetchTransUpdateTaskList?.[key] || "";

  const dispatch = useDispatch();
  const companyId = window.localStorage.getItem("companyid");
  const [data, setData] = useState([]);
  const initialStartDate = new Date(); // Current date
  const initialEndDate = new Date(
    initialStartDate.getTime() + 7 * 24 * 60 * 60 * 1000,
  ); // Next week

  // Calculate the start week based on the initial start date
  const initialStartWeek = getCurrentWeek(initialStartDate);
  // Calculate the end week based on the initial end date
  const initialEndWeek = initialStartWeek + 1; // Set end week as next week

  // Initialize state with the calculated values
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [startWeek, setStartWeek] = useState(initialStartWeek);
  const [endWeek, setEndWeek] = useState(initialEndWeek);

  // Update start and end weeks when dates change
  useEffect(() => {
    setStartWeek(getCurrentWeek(startDate));
    setEndWeek(getNextWeek(startDate, endDate)); // Initialize endWeek based on startDate and endDate
  }, [startDate, endDate]);

  useEffect(() => {
    // Fetch data when companyId changes or dates change
    if (companyId) {
      const payload = {
        companyId,
        startingdate: startDate,
        endingdate: endDate,
      };
      dispatch(FetchEquipmentTaskList(payload));
    }
  }, [dispatch, companyId, startDate, endDate]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (date.getTime() > endDate.getTime()) {
      setEndDate(new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000));
    }
  };

  const handleEndDateChange = (date) => {
    if (date.getTime() < startDate.getTime()) {
      // If the selected end date is before the start date, don't update the end date
      return;
    }
    setEndDate(date);
  };

  const fetchEquipmentListData = useSelector(
    (state) => state.user.fetchEquipmentListResponse,
  );
  useEffect(() => {
    const available =
      fetchEquipmentListData?.status === true
        ? fetchEquipmentListData.data || []
        : [];
    const items = new Map(
      [...selectedEquipments, ...available].map((item) => [
        String(item.equipmentid),
        item,
      ]),
    );
    setData(
      Array.from(items.values()).map((item) => ({
        ...item,
        isSelected: selectedEquipments.some(
          (selectedItem) =>
            String(selectedItem.equipmentid) === String(item.equipmentid),
        ),
      })),
    );
  }, [fetchEquipmentListData, selectedEquipments]);

  const AddMissingItems = () => {
    const checkedItems = data.filter((item) => item.isSelected);
    const newItems = checkedItems.filter(
      (item) =>
        !selectedEquipments.some(
          (selectedItem) =>
            String(selectedItem.equipmentid) === String(item.equipmentid),
        ),
    );
    const updatedSelectedEquipments = singleSelect
      ? checkedItems
      : [...selectedEquipments, ...newItems];
    setSelectedEquipments(updatedSelectedEquipments);
    onApply?.(updatedSelectedEquipments);
    setToggleEquipment();
  };

  const handleCheckboxChange = (equipmentid) => {
    if (singleSelect) {
      setData((prev) =>
        prev.map((item) => ({
          ...item,
          isSelected:
            item.equipmentid === equipmentid ? !item.isSelected : false,
        })),
      );
      return;
    }

    const updatedData = data.map((item) => {
      if (item.equipmentid === equipmentid) {
        // Toggle the isSelected state for the clicked item
        const isSelected = !item.isSelected;
        return { ...item, isSelected };
      }
      return item;
    });
    setData(updatedData);
  };

  return (
    <div className="equipmentList gap-0" style={{ ...style }}>
      <div className="equipmentList-title-wrp">
        <div className="equipmentList-title">
          <img
            src={proEquipmentsIcon}
            className="card-plus-img me-2"
            alt="totalStock"
          />
          <span>{t("Available Equipments")}</span>
        </div>
        <img
          alt=""
          className="x-icon-pop cursor-pointer"
          src={xIcon}
          onClick={() => {
            setToggleEquipment();
          }}
        />
      </div>
      <div className="filter-wrp py-4">
        <div>
          <img
            style={{ width: "18px", height: "20px" }}
            src={CalenderIcon}
            alt="calender"
          />
        </div>
        <div
          className=""
          id="contact_calender_task"
          style={{
            color: "black",
            display: "flex",
            flexDirection: "column",
            // alignItems: "center",
          }}
        >
          <span>Week {startWeek}</span>
          <AppDatePicker
            onSelect={(d, event) => {
              event.preventDefault();
            }}
            inputClassNames={"equipment-task-date cursor-pointer"}
            dateFormat="dd/MM/yyyy"
            className="calender-icon-date"
            onChange={handleStartDateChange}
            placeholderText={"00/00/0000"}
            selected={startDate}
          />
        </div>
        <div>To</div>
        <div
          className=""
          id="contact_calender_task"
          style={{
            color: "black",
            display: "flex",
            flexDirection: "column",
            // alignItems: "center",
          }}
        >
          <span>Week {endWeek}</span>
          <AppDatePicker
            onSelect={(d, event) => {
              event.preventDefault();
            }}
            inputClassNames={"equipment-task-date cursor-pointer"}
            dateFormat="dd/MM/yyyy"
            className="calender-icon-date"
            onChange={handleEndDateChange}
            placeholderText={"00/00/0000"}
            selected={endDate}
          />
        </div>
      </div>
      {data.length > 0 ? (
        <>
          <div className="list-wrp my-custom-scrollbar">
            {data.map((item, index) => (
              <div
                className="data-list"
                key={index}
                onClick={() => handleCheckboxChange(item.equipmentid)}
              >
                <img
                  className="plus-img"
                  src={item.isSelected ? CheckboxBlueIcon : CheckboxIcon}
                  alt="check-item"
                />
                <div className="data-list-item">
                  <span>{item.equipmentname}</span>
                  <img
                    className="equipment-img"
                    src={item.picture ?? Package}
                    alt="check-item"
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="apply-button-wrapper">
            <button
              type="button"
              className="btn add-button add-btn-disable"
              onClick={AddMissingItems}
              disabled={!data.some((val) => val.isSelected === true)}
            >
              {t("Apply")}
            </button>
          </div>
        </>
      ) : (
        <span className="apply-button-wrapper">
          {t("No Equipment data available")}
        </span>
      )}
    </div>
  );
};
export default TaskEquipmentList;
